/**
 * argumentationOutcome — pure helper for the contract argumentation
 * mini-game (Phase 3 bis, 03/06/2026).
 *
 * The mini-game presents the player with 3 arguments to pick from
 * (prix / délais / qualité / proximité). The chosen argument is
 * compared to the client's `primaryDriver` and `secondaryDriver`.
 *
 * Outcomes :
 *   - chosen === primaryDriver   → success    → +5% revenue (multiplier 1.05)
 *   - chosen === secondaryDriver → neutral    → 0% (multiplier 1.0)
 *   - chosen !== either          → failure    → −5% revenue (multiplier 0.95)
 *
 * The helper is deterministic and side-effect free.
 *
 * @param {string} argument          - 'prix' | 'délais' | 'qualité' | 'proximité'
 * @param {string} primaryDriver
 * @param {string} secondaryDriver
 * @returns {{ outcome: 'success'|'neutral'|'failure', multiplier: number, message: string }}
 */
export function argumentationOutcome(argument, primaryDriver, secondaryDriver) {
  if (!argument) {
    return { outcome: 'neutral', multiplier: 1, message: 'Aucun argument joué.' };
  }
  if (argument === primaryDriver) {
    return {
      outcome: 'success',
      multiplier: 1.05,
      message: `Excellent ! Le client valorise « ${primaryDriver} » en priorité. +5 % sur le tarif.`,
    };
  }
  if (argument === secondaryDriver) {
    return {
      outcome: 'neutral',
      multiplier: 1,
      message: `Argument acceptable — le client est sensible à « ${secondaryDriver} ». Tarif maintenu.`,
    };
  }
  return {
    outcome: 'failure',
    multiplier: 0.95,
    message: `Mauvais angle d'attaque — le client cherchait « ${primaryDriver} ». −5 % de remise concédée.`,
  };
}

/**
 * getClientDrivers — derives a stable {primary, secondary} pair from a
 * client + contract pair. Kept in sync with ContractDetailModal.
 *
 * Heuristic :
 *   - Public sector (segment 'PUB') → qualité, prix
 *   - Grand compte                 → délais, prix
 *   - PME                          → prix, délais
 *   - Override per category :
 *       Frigorifique → qualité, délais
 *       Spécialisé   → qualité, prix
 *       Export EU/Extra-UE → délais, prix
 *       RSE / Marchés publics → qualité, prix
 *       BTP          → prix, proximité
 *       Vrac         → prix, délais
 *       Messagerie   → délais, prix
 */
export function getClientDriversForContract(client, contract) {
  // Category override has priority — better narrative coherence.
  const cat = contract?.category;
  if (cat === 'Frigorifique')           return { primary: 'qualité',  secondary: 'délais' };
  if (cat === 'Spécialisé')             return { primary: 'qualité',  secondary: 'prix' };
  if (cat === 'Export EU')              return { primary: 'délais',   secondary: 'prix' };
  if (cat === 'Export Extra-UE')        return { primary: 'délais',   secondary: 'prix' };
  if (cat === 'RSE / Marchés publics')  return { primary: 'qualité',  secondary: 'prix' };
  if (cat === 'BTP / Matériaux')        return { primary: 'prix',     secondary: 'proximité' };
  if (cat === 'Vrac / Palettes')        return { primary: 'prix',     secondary: 'délais' };
  if (cat === 'Messagerie / Colis')     return { primary: 'délais',   secondary: 'prix' };

  // Fall back to client segment.
  if (client?.segment === 'PUB') return { primary: 'qualité', secondary: 'prix' };
  if (client?.segment === 'GC')  return { primary: 'délais',  secondary: 'prix' };
  return { primary: 'prix', secondary: 'délais' };
}

/**
 * Whether the player can skip the argumentation mini-game.
 * Returns true when at least one commercial support is hired :
 * Léa Bertrand, Marc Dupuis, or Sylvie Marchand.
 *
 * @param {Object} hiredSupport - state.hiredSupport
 * @returns {boolean}
 */
export function canSkipArgumentation(hiredSupport) {
  if (!hiredSupport) return false;
  return !!(hiredSupport['role-lea-bertrand']
         || hiredSupport['role-marc-dupuis']
         || hiredSupport['role-sylvie-marchand']);
}

/**
 * Pick 3 arguments to display (always include the primary, then pick 2
 * from the remaining 3 to add some bluff). Stable shuffle based on
 * contract instanceId so the choice doesn't reshuffle on re-render.
 */
const _ALL_ARGS = ['prix', 'délais', 'qualité', 'proximité'];

export function pickArgumentChoices(primaryDriver, instanceId = '') {
  const others = _ALL_ARGS.filter(a => a !== primaryDriver);
  // Seed shuffle from instanceId for stability.
  let h = 0;
  for (const ch of (instanceId || '')) h = (h * 31 + ch.charCodeAt(0)) | 0;
  const rand = () => {
    h = (h * 1103515245 + 12345) | 0;
    return (h & 0x7fffffff) / 0x7fffffff;
  };
  const picked = [primaryDriver];
  const shuffled = [...others].sort(() => rand() - 0.5).slice(0, 2);
  return [...picked, ...shuffled].sort(() => rand() - 0.5);
}
